import React, { useId, useState } from 'react';
import { paginationStrings as S } from './PaginationFlowBite.strings';
import { buildPageItems, buildPerPageOptions, parsePageInput } from './paginationItems';
import { formatCount } from '../utils/formatCount';

interface TablePagination {
  pageSize: number | string;
  total: number | string;
  lastPage: number | string;
  currentPage: number | string;
  onChange: (page: number) => void;
}

interface PaginationFlowBiteProps {
  tablePagination: TablePagination;
  handlePerPage: (selectedOption: { value: number; label: string }) => void;
  /** Draw a top rule; leave off when the table's last row already draws one. */
  withTopBorder?: boolean;
  /** The page sizes offered; defaults to DEFAULT_PER_PAGE_OPTIONS. */
  perPageOptions?: number[];
}

/** Shows the jump-to-page box only when the page list is long enough to need it. */
const JUMP_MIN_PAGES = 8;

/** Fills `{token}` placeholders of a copy template with React nodes. */
const fillTemplate = (template: string, values: Record<string, React.ReactNode>) =>
  template.split(/(\{\w+\})/).map((part, index) => {
    const token = /^\{(\w+)\}$/.exec(part)?.[1];
    return (
      <React.Fragment key={index}>
        {token && token in values ? values[token] : part}
      </React.Fragment>
    );
  });

const Chevron: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
  </svg>
);

const Divider = () => (
  <span aria-hidden="true" className="h-5 w-px bg-gray-200 max-[430px]:hidden" />
);

const PaginationFlowBite: React.FC<PaginationFlowBiteProps> = ({
  tablePagination,
  handlePerPage,
  withTopBorder = false,
  perPageOptions: perPageDefaults,
}) => {
  const {
    pageSize: perPage,
    total: totalCount,
    lastPage: lastPageNumber,
    currentPage,
    onChange,
  } = tablePagination;

  const idPrefix = useId();
  const perPageId = `${idPrefix}-per-page`;
  const jumpId = `${idPrefix}-jump`;
  const [jumpValue, setJumpValue] = useState('');

  const total = Number(totalCount) || 0;
  const numCurrentPage = Number(currentPage);
  const numPerPage = Number(perPage);
  const safeLastPage = Math.max(1, Number(lastPageNumber) || 1);
  const pageItems = buildPageItems(numCurrentPage, safeLastPage);
  const perPageOptions = buildPerPageOptions(numPerPage, perPageDefaults);

  const firstItemIndex = (numCurrentPage - 1) * numPerPage + 1;
  const lastItemIndex = Math.min(numCurrentPage * numPerPage, total);
  const range =
    total === 0
      ? formatCount(0)
      : S.range
          .replace('{first}', formatCount(firstItemIndex))
          .replace('{last}', formatCount(lastItemIndex));

  const goTo = (page: number) => {
    if (page !== numCurrentPage && page >= 1 && page <= safeLastPage) onChange(page);
  };

  const handleJump = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const page = parsePageInput(jumpValue, safeLastPage);
    if (page !== null) goTo(page);
    setJumpValue('');
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    handlePerPage({ value, label: String(value) });
  };

  const iconButton =
    'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-white';
  const pageButton =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-medium';

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-6 py-3.5 text-sm text-gray-700 max-[640px]:justify-center max-[640px]:px-4 ${
        withTopBorder ? 'border-t border-gray-200' : ''
      }`}
    >
      <div className="flex flex-wrap items-center justify-center gap-3">
        <label htmlFor={perPageId} className="text-gray-500">
          {S.rowsPerPage}
        </label>
        <select
          id={perPageId}
          value={numPerPage}
          onChange={handleSelectChange}
          className="h-9 w-[76px] rounded-lg border border-gray-300 bg-white py-0 pl-3 pr-8 text-sm text-gray-900 focus:border-gray-500 focus:ring-gray-500"
        >
          {perPageOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <Divider />
        <p data-testid="pagination-summary">
          {fillTemplate(S.summary, {
            range: <span className="font-semibold text-gray-900">{range}</span>,
            total: <span className="font-semibold text-gray-900">{formatCount(total)}</span>,
          })}
        </p>
      </div>
      <nav aria-label={S.navAria} className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={S.previousPage}
            className={iconButton}
            onClick={() => goTo(numCurrentPage - 1)}
            disabled={numCurrentPage <= 1}
          >
            <Chevron direction="left" />
          </button>
          {pageItems.map((item) =>
            typeof item === 'number' ? (
              <button
                key={item}
                type="button"
                aria-current={item === numCurrentPage ? 'page' : undefined}
                aria-label={(item === numCurrentPage ? S.currentPageAria : S.pageAria).replace(
                  '{page}',
                  String(item),
                )}
                className={`${pageButton} ${
                  item === numCurrentPage ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => goTo(item)}
              >
                {formatCount(item)}
              </button>
            ) : (
              <span key={item} aria-hidden="true" className="px-1 text-gray-500">
                …
              </span>
            ),
          )}
          <button
            type="button"
            aria-label={S.nextPage}
            className={iconButton}
            onClick={() => goTo(numCurrentPage + 1)}
            disabled={numCurrentPage >= safeLastPage}
          >
            <Chevron direction="right" />
          </button>
        </div>
        {safeLastPage >= JUMP_MIN_PAGES && (
          <>
            <Divider />
            <form onSubmit={handleJump} className="flex items-center gap-2">
              <label htmlFor={jumpId} className="text-gray-500">
                {S.goTo}
              </label>
              <div className="flex h-9 overflow-hidden rounded-lg border border-gray-300 focus-within:border-gray-500">
                <input
                  id={jumpId}
                  aria-label={S.goToPage}
                  type="number"
                  min={1}
                  max={safeLastPage}
                  inputMode="numeric"
                  placeholder={S.pagePlaceholder}
                  value={jumpValue}
                  onChange={(event) => setJumpValue(event.target.value)}
                  className="h-full w-16 border-0 px-2.5 text-sm text-gray-900 focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                {/* bg-[#f9fafb]: the theme remaps `gray-50` to white. */}
                <button
                  type="submit"
                  className="border-l border-gray-300 bg-[#f9fafb] px-3 text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  {S.go}
                </button>
              </div>
            </form>
          </>
        )}
      </nav>
    </div>
  );
};

export default PaginationFlowBite;
